/**
 * Pure helpers behind the DOM screens: name and chat rules mirrored from
 * the server's validate.ts, room-code hash parsing, number formatting, the
 * results table rows and the lobby start rule. No DOM access, so the node
 * test runner covers all of it.
 */

import type { LobbyPlayer, MatchResult } from '@shared/index.js';
import { DIFFICULTY, MAX_CHAT_LENGTH, MAX_NAME_LENGTH, ROOM_CODE_LENGTH, isRoomCode, randomWizardName } from '@shared/index.js';

/** localStorage keys shared by the screens (one place so nothing collides). */
export const STORAGE_KEYS = {
  name: 'spellweave.name',
  color: 'spellweave.color',
  muted: 'spellweave.muted',
  onboarded: 'spellweave.onboarded',
  spellbookTab: 'spellweave.spellbookTab',
  lobbyOpen: 'spellweave.lobbyOpen',
} as const;

/** Control characters, bidi and zero-width marks, line separators (same class as the server). */
const UNPRINTABLE = /[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u2028\u2029\u202a-\u202e\u2066-\u2069\ufeff]/g;

/** Cut to `max` UTF-16 units without leaving a dangling high surrogate. */
function cutSafe(s: string, max: number): string {
  if (s.length <= max) return s;
  let out = s.slice(0, max);
  const last = out.charCodeAt(out.length - 1);
  if (last >= 0xd800 && last <= 0xdbff) out = out.slice(0, -1);
  return out;
}

function printable(raw: string): string {
  return raw.replace(UNPRINTABLE, '').replace(/\s+/g, ' ').trim();
}

/** A printable, single-spaced name of at most MAX_NAME_LENGTH; '' when nothing usable is left. */
export function sanitizeName(raw: string): string {
  return cutSafe(printable(raw), MAX_NAME_LENGTH).trim();
}

/** A printable chat line of at most MAX_CHAT_LENGTH; '' means "nothing to send". */
export function sanitizeChat(raw: string): string {
  return cutSafe(printable(raw), MAX_CHAT_LENGTH).trim();
}

/**
 * The name to put in `hello`: the sanitized input, or a fun random one when
 * the field is blank (DESIGN 6.6). The server would also pick one, but doing
 * it here lets the landing page show and remember it.
 */
export function nameOrRandom(raw: string, rng: () => number = Math.random): string {
  const clean = sanitizeName(raw);
  return clean.length > 0 ? clean : randomWizardName(rng);
}

/** Robe colour index 0..3; anything else reads as 0. */
export function sanitizeColor(raw: unknown): number {
  const n = typeof raw === 'string' ? Number(raw) : raw;
  return typeof n === 'number' && Number.isInteger(n) && n >= 0 && n < 4 ? n : 0;
}

// ------------------------------------------------------------------ room codes

/** Uppercase letters only, cut to the code length: what the Join field shows while typing. */
export function normalizeCode(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z]/g, '').slice(0, ROOM_CODE_LENGTH);
}

/**
 * The room code in a location hash such as "#ABCDE", "#/abcde" or
 * "#code=ABCDE"; null when the hash holds no complete code.
 */
export function parseRoomHash(hash: string): string | null {
  let s = hash.trim();
  if (s.startsWith('#')) s = s.slice(1);
  while (s.startsWith('/') || s.startsWith('!')) s = s.slice(1);
  const eq = s.indexOf('=');
  if (eq >= 0) s = s.slice(eq + 1);
  const amp = s.indexOf('&');
  if (amp >= 0) s = s.slice(0, amp);
  const code = normalizeCode(s);
  return code.length === ROOM_CODE_LENGTH && isRoomCode(code) ? code : null;
}

/** Shareable link for a room (DESIGN 6.6 "Room URL /#CODE"). */
export function roomLink(code: string, loc: { origin: string; pathname: string }): string {
  return `${loc.origin}${loc.pathname}#${code}`;
}

// ------------------------------------------------------------------ numbers

/** 12345 -> "12,345"; rounds first so stats never show decimals. */
export function formatInt(n: number): string {
  if (!Number.isFinite(n)) return '0';
  const r = Math.round(n);
  const sign = r < 0 ? '-' : '';
  const digits = String(Math.abs(r));
  let out = '';
  for (let i = 0; i < digits.length; i++) {
    const fromEnd = digits.length - i;
    out += digits[i];
    if (fromEnd > 1 && fromEnd % 3 === 1) out += ',';
  }
  return sign + out;
}

/** Seconds -> "m:ss" for timers. */
export function formatClock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r < 10 ? '0' : ''}${r}`;
}

// ------------------------------------------------------------------ results (DESIGN 4.6, 6.6 step 5)

export interface ResultRow {
  id: number;
  name: string;
  kills: number;
  dmg: number;
  heal: number;
  ff: number;
  deaths: number;
  revives: number;
  /** Friendly-fire damage as a fraction of the table's worst offender (0..1); the "Oops" meter. */
  oops: number;
  /** Holder of the Menace title: most friendly-fire damage, and some. */
  menace: boolean;
  /** Capture the flag: the player's side. */
  team?: 0 | 1;
}

export const MENACE_TITLE = 'Menace';

/**
 * Table rows in server (join) order, a match's grouped by team. The Oops meter
 * is relative to the worst friendly-fire total; nobody is the Menace when
 * nobody hurt a friend.
 */
export function buildResultRows(result: MatchResult): ResultRow[] {
  let worst = 0;
  for (const p of result.players) worst = Math.max(worst, p.ff);
  const players = result.mode === 'ctf' ? [...result.players].sort((a, b) => (a.team ?? 0) - (b.team ?? 0)) : result.players;
  return players.map((p) => ({
    id: p.id,
    name: p.name,
    kills: p.kills,
    dmg: p.dmg,
    heal: p.heal,
    ff: p.ff,
    deaths: p.deaths,
    revives: p.revives,
    oops: worst > 0 ? p.ff / worst : 0,
    menace: p.ff > 0 && p.id === result.menace,
    ...(p.team !== undefined ? { team: p.team } : {}),
  }));
}

/** Who holds the Menace title, or null when nobody dealt friendly fire. */
export function menaceOf(result: MatchResult): { id: number; name: string } | null {
  const p = result.players.find((x) => x.id === result.menace);
  return p && p.ff > 0 ? { id: p.id, name: p.name } : null;
}

/** The two capture-the-flag teams by name (palette.ts TEAMS, Twin Fonts' bases). */
const CTF_TEAM_NAMES = ['Rose', 'Lime'] as const;

/** "Wave 7 reached" / "Wave 30 cleared": the results headline. Story: Mother Mire beaten, or how far the party got. */
export function resultHeadline(result: MatchResult, finalWave: number): string {
  if (result.mode === 'story') return result.won ? 'Mother Mire is beaten. The Everflame burns.' : `Rooms reached: ${result.wave}`;
  if (result.mode === 'ctf') {
    const [a, b] = result.teams ?? [0, 0];
    if (result.winner === 0 || result.winner === 1) return `${CTF_TEAM_NAMES[result.winner]} wins, ${Math.max(a, b)} to ${Math.min(a, b)}`;
    return `A draw, ${a} to ${b}`;
  }
  return result.wave >= finalWave ? `Wave ${result.wave} cleared` : `Wave ${result.wave} reached`;
}

/** The line under the headline: the team score in co-op; who played on which side in a match. */
export function resultSubline(result: MatchResult): string {
  if (result.mode !== 'ctf') return `${result.difficulty ? `${DIFFICULTY[result.difficulty].label}  ·  ` : ''}Team score ${formatInt(result.score)}`;
  const side = (t: 0 | 1): string => `${CTF_TEAM_NAMES[t]}: ${result.players.filter((p) => p.team === t).map((p) => p.name).join(', ') || 'nobody'}`;
  return `${side(0)}  ·  ${side(1)}`;
}

// ------------------------------------------------------------------ lobby (DESIGN 5.1, 6.6)

export interface StartState {
  isHost: boolean;
  /** Everyone connected other than the host is ready (the host's Start press counts as their own ready). */
  canStart: boolean;
  /** Names of connected players still not ready, for the button hint. */
  waitingOn: string[];
}

export function startState(players: readonly LobbyPlayer[], pid: number): StartState {
  const me = players.find((p) => p.id === pid);
  const isHost = me?.host === true;
  const waitingOn = players.filter((p) => p.connected && !p.ready && !p.host).map((p) => p.name);
  return { isHost, canStart: isHost && waitingOn.length === 0, waitingOn };
}

/** The last `n` entries, oldest first. */
export function lastLines<T>(lines: readonly T[], n: number): T[] {
  return n <= 0 ? [] : lines.slice(Math.max(0, lines.length - n));
}

// ------------------------------------------------------------------ error wording

/**
 * The toast for an `err` frame. `joining` is true while a create/join is in
 * flight; without one, `no_room` is about the room we were already in (after a
 * server restart the reconnect finds no session), not a mistyped code.
 */
export function describeError(code: string, msg: string, joining = true): string {
  switch (code) {
    case 'no_room': return joining ? 'No room with that code.' : 'Your room has closed (the server may have restarted). Create or join a new one.';
    case 'room_full': return 'That room is full (4 wizards max).';
    case 'room_limit': return 'The server has no free rooms right now. Try again in a minute.';
    case 'bad_version': return 'Your client is out of date. Refresh the page.';
    case 'not_host': return 'Only the host can start the match.';
    case 'not_ready': return msg || 'Everyone needs to be ready first.';
    case 'room_crashed': return 'The room hit an internal error and was closed.';
    case 'rate_limit': return 'Slow down: messages are being dropped.';
    default: return msg || code;
  }
}

/**
 * Why the server closed the connection for good (net/socket.ts FINAL_CLOSE_CODES), or null
 * when an `err` frame already said it (4001 arrives after `bad_version`).
 */
export function giveUpText(code: number): string | null {
  switch (code) {
    case 4003: return 'This game is now open in another tab or window.';
    case 4001: return null;
    default: return 'The server closed the connection. Wait a minute, then create or join a room again.';
  }
}
